import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import './NextCase.css'; // <--- CSS Import

const NextCase = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  // ----- Width of the page area (not the browser) - used only for responsive design -----
  const wrapRef = useRef(null);
  const [boxWidth, setBoxWidth] = useState(1200);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;

    const update = () => setBoxWidth(Math.round(el.getBoundingClientRect().width) || 1200);
    update();

    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(update);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const sizeClass =
    boxWidth >= 1000 ? "nc-lg" :
    boxWidth >= 760 ? "nc-md" :
    boxWidth >= 600 ? "nc-sm" :
    boxWidth >= 480 ? "nc-xs" : "nc-xxs";

  const rowsPerPage = 10;

  // Get userLabId from localStorage or context
  const userLabId = localStorage.getItem('userLabId') || 'defaultLabId';

  // Fetch data from API
  useEffect(() => {
    fetchRemakeTypes();
  }, []);

  const fetchRemakeTypes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`https://hms.backend.hiragroupcloud.com/api/remake-types?userLabId=${userLabId}`);

      if (response.data.success) {
        // Transform API data to match your frontend structure
        const transformedData = response.data.data.map(item => ({
          id: item._id,
          name: item.reason,
          procedure: "None",
          note: "Not Required",
          description: item.description || "Not Required",
        }));
        setData(transformedData);
      }
    } catch (error) {
      console.error("Error fetching remake types:", error);
      // Fallback to dummy data if API fails
      setData([
        { id: 1, name: "DENTIST WANTS A DIFFERENT SHADE", procedure: "None", note: "Not Required", description: "Not Required" },
        { id: 2, name: "Improve Contacts", procedure: "None", note: "Not Required", description: "Not Required" },
        { id: 3, name: "NEEDS ADJUSTING", procedure: "None", note: "Not Required", description: "Not Required" },
        { id: 4, name: "NEXT PROCEDURE", procedure: "None", note: "Not Required", description: "Not Required" },
        { id: 5, name: "PATIENT LOST IT", procedure: "None", note: "Not Required", description: "Not Required" },
        { id: 6, name: "REMAKE", procedure: "Same", note: "Not Required", description: "Required" },
        { id: 7, name: "REPAIR", procedure: "None", note: "Not Required", description: "Not Required" },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Filter data based on search term
  const filteredRaw = data.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination logic
  const totalFiltered = filteredRaw.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedData = filteredRaw.slice(startIndex, startIndex + rowsPerPage);

  const goToFirstPage = () => {
    setCurrentPage(1);
  };

  const goToPreviousPage = () => {
    const newPage = currentPage > 1 ? currentPage - 1 : 1;
    setCurrentPage(newPage);
  };

  const goToNextPage = () => {
    const newPage = currentPage < totalPages ? currentPage + 1 : totalPages;
    setCurrentPage(newPage);
  };

  const goToLastPage = () => {
    setCurrentPage(totalPages);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Edit button ke text par click -> page show with ID in URL
  const handleEditClick = (item) => {
    navigate(`/Editnextcase/${item.id}`, { state: { caseData: item } });
  };

  // Delete function - now calls API
  const handleDeleteClick = async (item) => {
    if (window.confirm(`Are you sure you want to delete "${item.name}"?`)) {
      try {
        const response = await axios.delete(`https://hms.backend.hiragroupcloud.com/api/remake-types/${item.id}`);

        if (response.data.success) {
          // Remove from local state
          const updatedData = data.filter((d) => d.id !== item.id);
          setData(updatedData);
          setOpenDropdownId(null);

          // Pagination adjust
          const newFilteredLength = updatedData.filter(d =>
            d.name.toLowerCase().includes(searchTerm.toLowerCase())
          ).length;
          const newTotalPages = Math.max(1, Math.ceil(newFilteredLength / rowsPerPage));
          if (currentPage > newTotalPages) {
            setCurrentPage(newTotalPages);
          }

          alert('Item deleted successfully!');
        }
      } catch (error) {
        console.error("Error deleting remake type:", error);
        alert('Failed to delete item. Please try again.');
      }
    }
  };

  const handleAddClick = () => {
    navigate("/AddnextCase");
  };

  const toggleDropdown = (event, index) => {
    event.stopPropagation();
    setOpenDropdownId(openDropdownId === index ? null : index);
  };

  // Click outside se dropdown close
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId !== null && !event.target.closest('.nc-dropdown-container')) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openDropdownId]);

  return (
    <div
      ref={wrapRef}
      className={`nc-container ${sizeClass}`}
      style={{ "--nc-w": boxWidth }}
    >
      <h1 className="nc-heading">Next-case Types</h1>

      <div className="nc-main-box">
        <div className="nc-inner-card">
          {/* Top Section */}
          <div className="nc-top-section">
            <div className="nc-search-box">
              <label className="nc-label">Search</label>
              <input
                type="text"
                className="nc-input"
                placeholder="Type to search"
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>

            <button className="nc-add-btn" onClick={handleAddClick}>
              <span className="nc-plus">+</span> Add
            </button>
          </div>

          <div className="nc-table">
            {/* Header Row */}
            <div className="nc-list-header">
              <div className="nc-col-name">Name<span className="nc-sort nc-sort-active"></span></div>
              <div className="nc-col-proc">Procedure<span className="nc-sort"></span></div>
              <div className="nc-col-note">Note</div>
              <div className="nc-col-desc">Description</div>
              <div className="nc-col-action"></div>
            </div>

            {/* Data Rows */}
            <div className="nc-rows">
              {loading ? (
                <div className="nc-loading">Loading...</div>
              ) : paginatedData.length > 0 ? (
                paginatedData.map((item, index) => (
                  <div key={item.id} className="nc-list-row">
                    <div className="nc-col-name">{item.name}</div>
                    <div className="nc-col-proc">{item.procedure}</div>
                    <div className="nc-col-note">{item.note}</div>
                    <div className="nc-col-desc">{item.description}</div>
                    <div className="nc-col-action">
                      {/* Edit Button - andar icon hai */}
                      <div className="nc-dropdown-container">
                        <button
                          className="nc-edit-btn"
                          onClick={(e) => {
                            // Agar icon par click hua to dropdown khulega, warna page show hoga
                            if (e.target.closest('.dropdown-icon')) {
                              toggleDropdown(e, index);
                            } else {
                              handleEditClick(item);
                            }
                          }}
                        >
                          <span className="nc-edit-text">Edit</span>
                          <span className="dropdown-icon">
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                              style={{
                                transform: openDropdownId === index ? 'rotate(180deg)' : 'rotate(0deg)',
                              }}
                            >
                              <path d="M7 10l5 5 5-5z"></path>
                            </svg>
                          </span>
                        </button>

                        {/* Dropdown Menu - Sirf Delete */}
                        {openDropdownId === index && (
                          <div className="nc-dropdown-menu">
                            <button
                              className="nc-dropdown-item"
                              onClick={() => handleDeleteClick(item)}
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="nc-empty">No matching records found</div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="nc-pagination">
            <p className="nc-page-text">
              Showing {startIndex + 1} to{" "}
              {Math.min(startIndex + rowsPerPage, totalFiltered)} of {totalFiltered}{" "}
              entries
            </p>

            <div className="nc-pagination-btns">
              <button className="nc-page-btn" onClick={goToFirstPage}>First</button>
              <button className="nc-page-btn" onClick={goToPreviousPage}>Previous</button>
              <button className="nc-page-btn nc-page-active" onClick={() => {}}>
                {currentPage}
              </button>
              <button className="nc-page-btn" onClick={goToNextPage}>Next</button>
              <button className="nc-page-btn" onClick={goToLastPage}>Last</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NextCase;